import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography
} from '@mui/material';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';

import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';

type AgendaDay = {
  date: string;
  daytime_dogs: number;
  overnight_dogs: number;
  daycare_blocked: boolean;
  overnight_blocked: boolean;
};

type AgendaDog = {
  id: string;
  name: string;
  breed: string | null;
  sex: string | null;
  active: boolean;
  capacity_exception: boolean;
};

type AgendaReservation = {
  id: string;
  family_id: string;
  booking_type: string;
  start_date: string;
  end_date: string;
  arrival_time: string | null;
  departure_time: string | null;
  status: string;
  option_expires_at: string | null;
  total_amount: number | null;
  counts_for_capacity: boolean;
  customer: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  dogs: AgendaDog[];
  shortening?: any;
};

type AgendaHold = {
  reservation_id: string;
  care_date: string;
  daytime: boolean;
  overnight: boolean;
  dog_names: string;
  amount_to_release: number;
};

type AgendaBlock = {
  id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  blocks_daycare: boolean;
  blocks_overnight: boolean;
};

type AgendaPayload = {
  version: number;
  generated_at: string;
  from_date: string;
  through_date: string;
  days: AgendaDay[];
  reservations: AgendaReservation[];
  shortening_holds: AgendaHold[];
  blocks: AgendaBlock[];
};

const WEEKDAYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

function isoDate(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDate(value: string) {
  return new Date(`${value}T12:00:00`);
}

function monthStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function monthEnd(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function fullDate(value: string) {
  return new Intl.DateTimeFormat('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(parseDate(value));
}

function monthTitle(date: Date) {
  const text = new Intl.DateTimeFormat('nl-NL', {
    month: 'long',
    year: 'numeric'
  }).format(date);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function money(value: number) {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(Number(value || 0));
}

function shortTime(value: string | null) {
  if (!value) return null;
  return value.slice(0, 5);
}

function statusLabel(status: string) {
  return ({
    confirmed: 'Bevestigd',
    option: 'Optie',
    cancel_pending_payment: 'Wacht op betaling',
    cancelled: 'Geannuleerd',
    completed: 'Afgerond',
    expired: 'Verlopen'
  } as Record<string, string>)[status] ?? status;
}

function customerName(r: AgendaReservation) {
  const value = `${r.customer?.first_name ?? ''} ${r.customer?.last_name ?? ''}`.trim();
  return value || r.customer?.email || 'Onbekende klant';
}

function occupancyLabel(day: AgendaDay) {
  if (day.daycare_blocked && day.overnight_blocked) return 'Geblokkeerd';
  if (day.daycare_blocked || day.overnight_blocked) return 'Deels geblokkeerd';

  const highest = Math.max(Number(day.daytime_dogs || 0), Number(day.overnight_dogs || 0));
  if (highest >= 4) return 'Bestaande bezetting boven nieuwe limiet';
  if (highest === 3) return 'Derde plek gebruikt';
  if (highest === 2) return 'Normale capaciteit vol';
  return 'Ruimte';
}

function occupancyColor(day: AgendaDay): 'default' | 'error' {
  if (day.daycare_blocked || day.overnight_blocked) return 'error';
  const highest = Math.max(Number(day.daytime_dogs || 0), Number(day.overnight_dogs || 0));
  if (highest >= 4) return 'error';
  return 'default';
}

function occupancyChipSx(day: AgendaDay) {
  if (day.daycare_blocked || day.overnight_blocked) {
    return { fontWeight: 700 };
  }

  const highest = Math.max(Number(day.daytime_dogs || 0), Number(day.overnight_dogs || 0));

  if (highest >= 4) {
    return { fontWeight: 700 };
  }

  if (highest === 3) {
    return {
      bgcolor: '#8E6BBE',
      color: '#ffffff',
      fontWeight: 700,
      '&:hover': { bgcolor: '#8E6BBE' }
    };
  }

  if (highest === 2) {
    return {
      bgcolor: '#F8A5B6',
      color: '#3B2B31',
      fontWeight: 700,
      '&:hover': { bgcolor: '#F8A5B6' }
    };
  }

  return {
    bgcolor: '#D7E4C0',
    color: '#2E3827',
    fontWeight: 700,
    '&:hover': { bgcolor: '#D7E4C0' }
  };
}

export const AdminAgendaPage = () => {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => monthStart(today));
  const [selectedDate, setSelectedDate] = useState(() => isoDate(today));
  const [agenda, setAgenda] = useState<AgendaPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const start = useMemo(() => monthStart(cursor), [cursor]);
  const end = useMemo(() => monthEnd(cursor), [cursor]);

  useEffect(() => {
    const startIso = isoDate(start);
    const endIso = isoDate(end);

    if (selectedDate < startIso || selectedDate > endIso) {
      setSelectedDate(startIso);
    }

    loadAgenda(startIso, endIso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start.getFullYear(), start.getMonth()]);

  async function loadAgenda(startIso: string, endIso: string) {
    if (!supabase) return;

    setLoading(true);
    setError('');

    const { data, error: rpcError } = await supabase.rpc(
      'admin_get_agenda',
      {
        p_start_date: startIso,
        p_end_date: endIso
      }
    );

    if (rpcError) {
      setError(`Agenda kon niet worden geladen: ${rpcError.message}`);
      setAgenda(null);
    } else {
      setAgenda((data ?? null) as AgendaPayload | null);
    }

    setLoading(false);
  }

  const dayMap = useMemo(() => {
    const map = new Map<string, AgendaDay>();
    (agenda?.days ?? []).forEach(day => map.set(day.date, day));
    return map;
  }, [agenda]);

  const selectedDay = dayMap.get(selectedDate) ?? null;

  const selectedReservations = useMemo(() => {
    return (agenda?.reservations ?? [])
      .filter(r => r.start_date <= selectedDate && r.end_date >= selectedDate)
      .sort((a, b) => {
        const byTime = (a.arrival_time ?? '99:99').localeCompare(b.arrival_time ?? '99:99');
        if (byTime !== 0) return byTime;
        return customerName(a).localeCompare(customerName(b), 'nl');
      });
  }, [agenda, selectedDate]);

  const selectedHolds = useMemo(
    () => (agenda?.shortening_holds ?? []).filter(h => h.care_date === selectedDate),
    [agenda, selectedDate]
  );

  const selectedBlocks = useMemo(
    () => (agenda?.blocks ?? []).filter(b => b.start_date <= selectedDate && b.end_date >= selectedDate),
    [agenda, selectedDate]
  );

  const offset = (start.getDay() + 6) % 7;
  const calendarCells = [
    ...Array.from({ length: offset }, (_, i) => ({ key: `empty-${i}`, day: null as AgendaDay | null })),
    ...(agenda?.days ?? []).map(day => ({ key: day.date, day }))
  ];

  function goToday() {
    const now = new Date();
    setCursor(monthStart(now));
    setSelectedDate(isoDate(now));
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffafb', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
          gap={2}
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Pippaya Hondenopvang · beheer
            </Typography>
            <Typography variant="h3">Agenda & bezetting</Typography>
            <Typography color="text.secondary">
              Echte reserveringen, blokkades en bezetting uit Supabase.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate('/beheer')}
            >
              Terug naar beheer
            </Button>
            <Button variant="outlined" onClick={goToday}>
              Vandaag
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mt: 3 }}>
          Normaal maximaal 2 honden. Een derde plek kan alleen via een uitzonderingshond of handmatig door Pippaya-beheer.
          Nieuwe boekingen mogen nooit boven 3 uitkomen. Bestaande boekingen met een hogere bezetting blijven zichtbaar en onaangetast.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mt: 3, p: { xs: 1.5, md: 2.5 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Button
              variant="outlined"
              onClick={() => setCursor(value => addMonths(value, -1))}
              startIcon={<ArrowBackIosNewRoundedIcon />}
            >
              Vorige
            </Button>

            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonthRoundedIcon />
              <Typography variant="h5">{monthTitle(cursor)}</Typography>
            </Stack>

            <Button
              variant="outlined"
              onClick={() => setCursor(value => addMonths(value, 1))}
              endIcon={<ArrowForwardIosRoundedIcon />}
            >
              Volgende
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Stack alignItems="center" py={8} spacing={2}>
              <CircularProgress />
              <Typography color="text.secondary">Agenda laden…</Typography>
            </Stack>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: 930 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    gap: 1
                  }}
                >
                  {WEEKDAYS.map(day => (
                    <Typography
                      key={day}
                      variant="overline"
                      color="text.secondary"
                      sx={{ px: 1, textAlign: 'center' }}
                    >
                      {day}
                    </Typography>
                  ))}

                  {calendarCells.map(cell => {
                    if (!cell.day) {
                      return <Box key={cell.key} sx={{ minHeight: 126 }} />;
                    }

                    const day = cell.day;
                    const selected = day.date === selectedDate;
                    const date = parseDate(day.date);
                    const isToday = day.date === isoDate(today);

                    return (
                      <Paper
                        key={cell.key}
                        variant="outlined"
                        onClick={() => setSelectedDate(day.date)}
                        sx={{
                          p: 1.25,
                          minHeight: 126,
                          cursor: 'pointer',
                          boxShadow: selected ? 2 : 'none',
                          borderWidth: selected ? 2 : 1,
                          borderColor: selected ? 'primary.main' : 'divider'
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography fontWeight={800}>
                            {date.getDate()}
                          </Typography>
                          {isToday && <Chip size="small" label="vandaag" />}
                        </Stack>

                        <Stack spacing={0.65} sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            Overdag: <b>{day.daytime_dogs}</b>
                          </Typography>
                          <Typography variant="body2">
                            Nacht: <b>{day.overnight_dogs}</b>
                          </Typography>
                          <Chip
                            size="small"
                            color={occupancyColor(day)}
                            label={occupancyLabel(day)}
                            sx={{ ...occupancyChipSx(day), alignSelf: 'flex-start', maxWidth: '100%' }}
                          />
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}
        </Card>

        <Card sx={{ mt: 3, p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" sx={{ textTransform: 'capitalize' }}>
            {fullDate(selectedDate)}
          </Typography>

          {selectedDay && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
              <Chip label={`Overdag ${selectedDay.daytime_dogs} hond(en)`} />
              <Chip label={`Nacht ${selectedDay.overnight_dogs} hond(en)`} />
              <Chip
                color={occupancyColor(selectedDay)}
                label={occupancyLabel(selectedDay)}
                sx={occupancyChipSx(selectedDay)}
              />
            </Stack>
          )}

          {selectedBlocks.map(block => (
            <Alert key={block.id} severity="warning" sx={{ mt: 2 }}>
              <b>Blokkade:</b> {block.reason || 'Niet beschikbaar'} ·
              {block.blocks_daycare ? ' dagopvang geblokkeerd' : ''}
              {block.blocks_daycare && block.blocks_overnight ? ' en' : ''}
              {block.blocks_overnight ? ' logeren geblokkeerd' : ''}.
            </Alert>
          ))}

          {selectedHolds.map(hold => (
            <Alert key={`${hold.reservation_id}-${hold.care_date}`} severity="warning" sx={{ mt: 2 }}>
              <b>Inkorting houdt deze plek nog vast:</b> {hold.dog_names}.
              {Number(hold.amount_to_release || 0) > 0
                ? ` Vrijgave na dekking van ${money(hold.amount_to_release)}.`
                : ' De plek kan worden vrijgegeven.'}
            </Alert>
          ))}

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="h5">Reserveringen op deze dag</Typography>

          {!selectedReservations.length ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Geen reserveringen op deze dag.
            </Alert>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {selectedReservations.map(r => {
                const arrival = r.start_date === selectedDate;
                const departure = r.end_date === selectedDate;

                return (
                  <Paper key={r.id} variant="outlined" sx={{ p: 2, boxShadow: 'none' }}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography fontWeight={800}>{customerName(r)}</Typography>
                          <Chip size="small" label={statusLabel(r.status)} />
                          {!r.counts_for_capacity && (
                            <Chip size="small" variant="outlined" label="telt niet mee voor capaciteit" />
                          )}
                        </Stack>

                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                          {r.booking_type === 'dagopvang' ? 'Dagopvang' : 'Logeren'} · {r.start_date} t/m {r.end_date}
                        </Typography>

                        {(arrival || departure) && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {arrival && `Brengen${shortTime(r.arrival_time) ? ` ${shortTime(r.arrival_time)} uur` : ''}`}
                            {arrival && departure ? ' · ' : ''}
                            {departure && `Ophalen${shortTime(r.departure_time) ? ` ${shortTime(r.departure_time)} uur` : ''}`}
                          </Typography>
                        )}

                        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                          {(r.dogs ?? []).map(dog => (
                            <Chip
                              key={dog.id}
                              size="small"
                              icon={<PetsRoundedIcon />}
                              label={dog.capacity_exception ? `${dog.name} · uitzondering` : dog.name}
                              color="default"
                              sx={
                                dog.capacity_exception
                                  ? {
                                      bgcolor: '#E9DFF6',
                                      color: '#5D437A',
                                      fontWeight: 700,
                                      '& .MuiChip-icon': {
                                        color: '#7A5AA6'
                                      },
                                      '&:hover': {
                                        bgcolor: '#E9DFF6'
                                      }
                                    }
                                  : undefined
                              }
                            />
                          ))}
                        </Stack>
                      </Box>

                      <Box sx={{ minWidth: { md: 180 }, textAlign: { md: 'right' } }}>
                        {r.total_amount !== null && (
                          <Typography fontWeight={800}>{money(r.total_amount)}</Typography>
                        )}
                        {r.customer?.phone && (
                          <Typography variant="body2" color="text.secondary">
                            {r.customer.phone}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Card>
      </Container>
    </Box>
  );
};
